import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Alert, Box, Button, Chip, CircularProgress, Snackbar, Tab, Tabs} from "@mui/material";
import Editor, {type Monaco} from "@monaco-editor/react";
import type {editor} from "monaco-editor";
import * as yaml from "js-yaml";
import {useModuleEditorApi} from "./api";
import {useLocalDraft} from "./useLocalDraft";
import {moduleTypeDefs} from "./moduleTypeDefs";
import type {ModuleDetail, ModulePrototype} from "./types";

type TabDef = {
    key: string;
    label: string;
    language: string;
};

const TABS: TabDef[] = [
    {key: "meta", label: "meta.yaml", language: "yaml"},
    {key: "node", label: "node.yaml", language: "yaml"},
    {key: "schema", label: "schema.yaml", language: "yaml"},
    {key: "script", label: "script.ts", language: "typescript"},
];

function moduleToTabs(mod: ModuleDetail): Record<string, string> {
    var meta = {
        module_name: mod.module_name,
        scope: mod.scope,
        description_md: mod.description_md ?? "",
        settings: mod.settings,
    };

    return {
        meta: yaml.dump(meta, {lineWidth: -1}),
        node: yaml.dump(mod.node_props ?? {}, {lineWidth: -1}),
        schema: yaml.dump(mod.edit_schema ?? {}, {lineWidth: -1}),
        script: mod.script,
    };
}

function tabsToPrototype(tabs: Record<string, string>): ModulePrototype {
    var meta = yaml.load(tabs.meta) as any ?? {};
    var nodeProps = yaml.load(tabs.node) as any ?? null;
    var editSchema = yaml.load(tabs.schema) as any ?? null;

    if (nodeProps && Object.keys(nodeProps).length === 0) nodeProps = null;
    if (editSchema && Object.keys(editSchema).length === 0) editSchema = null;

    return {
        module_name: meta.module_name ?? "",
        scope: meta.scope ?? "private",
        description_md: meta.description_md ?? "",
        settings: meta.settings ?? null,
        edit_schema: editSchema,
        node_props: nodeProps,
        script: tabs.script ?? "",
    };
}

function getModuleQueryKey(namespace: string, moduleId: string) {
    return ["module-editor-detail", namespace, moduleId];
}

export function ModuleEditorPage() {
    const {namespace, moduleId} = useParams<{ namespace: string; moduleId: string }>();
    const api = useModuleEditorApi();
    const client = useQueryClient();
    const monacoRef = useRef<Monaco | null>(null);
    const saveRef = useRef<() => void>(() => {});

    const [activeTab, setActiveTab] = useState(0);
    const [tabContents, setTabContents] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState<{ message: string; severity: "success" | "error" } | null>(null);

    var draft = useLocalDraft(namespace, moduleId);

    const {isFetching, isError, error, data: moduleData} = useQuery(
        getModuleQueryKey(namespace, moduleId),
        () => api.getModule(namespace, moduleId),
        {staleTime: 30000}
    );

    useEffect(() => {
        if (moduleData) {
            var serverTabs = moduleToTabs(moduleData);
            var localDraft = draft.loadDraft();

            if (localDraft && JSON.stringify(localDraft.tabs) !== JSON.stringify(serverTabs)) {
                draft.setHasDraftConflict(true);
            } else {
                draft.setHasDraftConflict(false);
                setTabContents(serverTabs);
            }

            if (!localDraft) {
                setTabContents(serverTabs);
            }
        }
    }, [moduleData]); // eslint-disable-line react-hooks/exhaustive-deps

    var handleEditorChange = useCallback((value: string | undefined) => {
        if (value === undefined) return;
        var currentTabKey = TABS[activeTab].key;
        setTabContents(prev => {
            var next = {...prev, [currentTabKey]: value};
            draft.saveDraft(next);
            return next;
        });
    }, [activeTab, draft]);

    function handleApplyDraft() {
        var localDraft = draft.loadDraft();
        if (localDraft) {
            setTabContents(localDraft.tabs);
            draft.setHasDraftConflict(false);
        }
    }

    function handleDiscardDraft() {
        draft.clearDraft();
        if (moduleData) {
            setTabContents(moduleToTabs(moduleData));
        }
    }

    async function handleSave() {
        try {
            setSaving(true);
            var prototype = tabsToPrototype(tabContents);
            var result = await api.updateModule(namespace, moduleId, prototype);
            if (!result) return;
            draft.clearDraft();
            await client.invalidateQueries(getModuleQueryKey(namespace, moduleId));
            await client.invalidateQueries(["module-editor-list"]);
            setSnackbar({message: "Module saved", severity: "success"});
        } catch (e: any) {
            setSnackbar({message: e.message || "Save failed", severity: "error"});
        } finally {
            setSaving(false);
        }
    }

    async function handlePromote(target: string) {
        if (!moduleData) return;
        var versions = moduleData as any;
        var headVersionId = String(versions.version_id);

        try {
            setSaving(true);
            await api.promoteModule(namespace, moduleId, headVersionId, target);
            await client.invalidateQueries(getModuleQueryKey(namespace, moduleId));
            await client.invalidateQueries(["module-editor-list"]);
            setSnackbar({message: `Promoted to ${target}`, severity: "success"});
        } catch (e: any) {
            setSnackbar({message: e.message || "Promote failed", severity: "error"});
        } finally {
            setSaving(false);
        }
    }

    saveRef.current = handleSave;

    useEffect(() => {
        function onKeyDown(e: KeyboardEvent) {
            if ((e.metaKey || e.ctrlKey) && e.key === "s") {
                e.preventDefault();
                saveRef.current();
            }
        }
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    var handleEditorMount = useCallback((editorInstance: editor.IStandaloneCodeEditor, monaco: Monaco) => {
        monacoRef.current = monaco;
        monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
            noSemanticValidation: true,
            noSyntaxValidation: false,
        });
        monaco.languages.typescript.typescriptDefaults.addExtraLib(moduleTypeDefs, "ts:module-globals.d.ts");

        editorInstance.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
            saveRef.current();
        });
    }, []);

    var currentTab = TABS[activeTab];
    var currentContent = tabContents[currentTab.key] ?? "";

    var editorOptions = useMemo(() => ({
        minimap: {enabled: false},
        fontSize: 14,
        lineNumbers: "on" as const,
        scrollBeyondLastLine: false,
        automaticLayout: true,
        tabSize: 2,
    }), []);

    if (isFetching && !moduleData) {
        return (
            <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", height: "60vh"}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (isError) {
        return <Alert severity="error">Failed to load module: {String(error)}</Alert>;
    }

    if (!moduleData) {
        return <Alert severity="warning">Module not found</Alert>;
    }

    return (
        <Box sx={{display: "flex", flexDirection: "column", height: "calc(100vh - 64px)", px: 2, pt: 2, pb: 0}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 2, mb: 1}}>
                <Box sx={{flex: 1}}>
                    <Box sx={{fontSize: "1.2em", fontWeight: "bold"}}>{moduleData.module_name}</Box>
                    <Box sx={{fontSize: "0.85em", opacity: 0.7}}>{namespace}/{moduleId}</Box>
                </Box>
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={saving}
                >
                    Save
                </Button>
                {moduleData.is_test
                    ? <Chip label="Promoted to Test" size="small" color="warning" variant="outlined"/>
                    : <Button
                        variant="contained"
                        size="small"
                        color="warning"
                        onClick={() => handlePromote("TEST")}
                        disabled={saving}
                    >
                        Promote to Test
                    </Button>
                }
                {moduleData.is_prod
                    ? <Chip label="Promoted to Prod" size="small" color="success" variant="outlined"/>
                    : <Button
                        variant="contained"
                        size="small"
                        color="success"
                        onClick={() => handlePromote("PROD")}
                        disabled={saving}
                    >
                        Promote to Prod
                    </Button>
                }
            </Box>

            {draft.hasDraftConflict && (
                <Alert severity="info" sx={{mb: 1}} action={
                    <Box sx={{display: "flex", gap: 1}}>
                        <Button size="small" onClick={handleApplyDraft}>Keep Local</Button>
                        <Button size="small" onClick={handleDiscardDraft}>Discard</Button>
                    </Box>
                }>
                    You have unsaved local changes from{" "}
                    {draft.draftTimestamp ? new Date(draft.draftTimestamp).toLocaleString() : "earlier"}.
                </Alert>
            )}

            <Tabs value={activeTab} onChange={(_, idx) => setActiveTab(idx)} sx={{mb: 1}}>
                {TABS.map(tab => <Tab key={tab.key} label={tab.label}/>)}
            </Tabs>

            <Box sx={{flex: 1, border: "1px solid #ccc", borderRadius: 1, overflow: "hidden"}}>
                <Editor
                    language={currentTab.language}
                    value={currentContent}
                    onChange={handleEditorChange}
                    onMount={handleEditorMount}
                    theme="light"
                    options={editorOptions}
                />
            </Box>

            <Snackbar
                open={snackbar !== null}
                autoHideDuration={3000}
                onClose={() => setSnackbar(null)}
                message={snackbar?.message}
            />
        </Box>
    );
}
